import React from "react";

import {
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { makeStyles } from "tss-react/mui";
import { ExpandMore } from "@mui/icons-material";

import { InfoCard, InfoCardProps } from "./InfoCard";
import { DataTable, DataTableProps } from "components/dashboard/DataTable";

const useStyles = makeStyles()((theme) => ({
  outer: {
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.secondary.light}`,
    overflow: "hidden",
  },
  title: {
    padding: theme.spacing(1),
  },
  body: {
    padding: theme.spacing(1),
  },
  accordion: {
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: theme.spacing(1),
    width: "100%",
  },
}));

export const InvoiceDetailCard = ({
  title,
  meaning,
  cardData,
  calculation,
  variant,
  calcSteps,
}: InvoiceDetailCardProps) => {
  const { classes } = useStyles();
  const theme = useTheme();

  const bgColor = theme.palette[variant].light;

  return (
    <Grid container direction="column" className={classes.outer}>
      <Grid className={classes.title} sx={{ bgcolor: bgColor }}>
        <Typography color={`${variant}.main`} variant="body2">
          {title}
        </Typography>
      </Grid>
      <Grid className={classes.body} container>
        <Typography variant="body2">O que significa?</Typography>
        <Typography> {meaning} </Typography>
        <Grid container gap={1} sx={{ mt: 1, mb: 1 }}>
          {cardData &&
            cardData.map((item) => <InfoCard key={item.title} {...item} />)}
        </Grid>
        <Grid container gap={1} sx={{ mt: 1, mb: 1 }}>
          {calcSteps &&
            calcSteps.map((step) => {
              if (typeof step === "string") {
                // render text-only information
                return <Typography key={step}>{step}</Typography>;
              } else {
                // anything other than pure text will be rendered inside a closed
                // accordion to hide it from the user, even if the summary it's also
                // just text
                const { title } = step;
                return (
                  <Accordion key={title} className={classes.accordion}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography
                        variant="body1"
                        component="p"
                        color="textPrimary"
                      >
                        {title}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {"summary" in step && (
                        <Typography variant="body1">{step.summary}</Typography>
                      )}
                      {"items" in step && <DataTable data={step.items} />}
                    </AccordionDetails>
                  </Accordion>
                );
              }
            })}
        </Grid>
        {calculation && (
          <Typography variant="body2">Como foi calculado?</Typography>
        )}
        {calculation && typeof calculation === "string" && (
          <Typography>{calculation}</Typography>
        )}
        {calculation && typeof calculation !== "string" && (
          <DataTable data={calculation} />
        )}
      </Grid>
    </Grid>
  );
};

export type InvoiceDetailCardProps = {
  /* The alert/consistency/inconsistency title */
  title: string;
  /* Explains what the topic is about */
  meaning: string;
  /* Color scheme to be used */
  variant: "success" | "warning" | "error";
  /* Displays real vs expected values */
  cardData?: Array<InfoCardProps>;
  /* Main calculation that is rendered directly in the card's body (always visible) */
  calculation?: string | DataTableProps["data"];
  /* Steps used to reach the main calculation. If plain text, will be rendered directly on the card.
  Else, it will be rendered inside a closed accordion */
  calcSteps?: Array<
    | string
    | { title: string; summary: string }
    | { title: string; items: DataTableProps["data"] }
  >;
};
